import React, { useState, useEffect, useMemo } from 'react';
import { getWorkloadDashboard, updatePMCapacity } from '../api';
import { useTheme } from '../hooks/useTheme';

/* ═══ Color Palette ═══ */
const P = {
  indigo: '#6366f1', indigoL: '#a5b4fc', indigoBg: 'rgba(99,102,241,.08)',
  emerald: '#10b981', emeraldL: '#6ee7b7', emeraldBg: 'rgba(16,185,129,.08)',
  amber: '#f59e0b', amberL: '#fcd34d', amberBg: 'rgba(245,158,11,.08)',
  rose: '#f43f5e', roseL: '#fda4af', roseBg: 'rgba(244,63,94,.08)',
  sky: '#0ea5e9', skyBg: 'rgba(14,165,233,.08)',
};

/* ═══ Formatters ═══ */
const $f = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(+v || 0);
const $s = (v) => { const n = +v || 0; if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`; if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`; return `$${n.toFixed(0)}`; };
const fmtMonth = (ym) => { if (!ym) return ''; const [y, m] = ym.split('-'); return `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][+m - 1]} '${y.slice(2)}`; };

const fmtDate = (d) => {
  if (!d) return '–';
  try {
    const dt = new Date(d + 'T00:00:00');
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return d; }
};

const daysLabel = (days) => {
  if (days <= 0) return 'Now';
  if (days === 1) return '1 day';
  if (days < 7) return `${days} days`;
  if (days < 30) return `${Math.ceil(days / 7)} weeks`;
  return `${Math.round(days / 30)} months`;
};

const urgencyColor = (days) => {
  if (days <= 0) return P.rose;
  if (days <= 7) return P.amber;
  if (days <= 30) return P.sky;
  return P.emerald;
};

const urgencyBg = (days) => {
  if (days <= 0) return P.roseBg;
  if (days <= 7) return P.amberBg;
  if (days <= 30) return P.skyBg;
  return P.emeraldBg;
};

const scoreColor = (score) => {
  if (score >= 70) return P.rose;
  if (score >= 40) return P.amber;
  return P.emerald;
};

/* ═══ Workload Bar ═══ */
const WorkloadBar = ({ score, t }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <div style={{
      width: 80, height: 8, backgroundColor: t.dark ? '#2d3748' : '#e2e8f0',
      borderRadius: 4, overflow: 'hidden', flexShrink: 0,
    }}>
      <div style={{
        width: `${Math.min(score, 100)}%`, height: '100%',
        backgroundColor: scoreColor(score), borderRadius: 4,
        transition: 'width 0.4s ease',
      }} />
    </div>
    <span style={{ fontSize: 12, fontWeight: 700, color: scoreColor(score), minWidth: 28 }}>
      {score}
    </span>
  </div>
);

/* ═══ Capacity Bar ═══ */
const CapacityBar = ({ current, cap, onCapChange, t }) => {
  const [editing, setEditing] = React.useState(false);
  const [editValue, setEditValue] = React.useState('');
  const inputRef = React.useRef(null);

  const hasCap = cap > 0;
  const pct = hasCap ? Math.min((current / cap) * 100, 120) : 0;
  const ratio = hasCap ? current / cap : 0;
  const barColor = ratio >= 1 ? P.rose : ratio >= 0.8 ? P.amber : P.emerald;

  const startEdit = (e) => {
    e.stopPropagation();
    setEditing(true);
    setEditValue(cap > 0 ? (cap / 1e6).toFixed(1) : '');
    setTimeout(() => inputRef.current?.select(), 50);
  };

  const saveEdit = () => {
    setEditing(false);
    const parsed = parseFloat(editValue);
    if (!isNaN(parsed) && parsed >= 0) {
      onCapChange(parsed * 1e6);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') saveEdit();
    if (e.key === 'Escape') setEditing(false);
  };

  return (
    <div style={{ minWidth: 160 }} onClick={(e) => e.stopPropagation()}>
      {/* Fraction display */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 4 }}>
        <span style={{ fontSize: 14, fontWeight: 800, color: hasCap ? barColor : t.textMuted }}>
          {$s(current)}
        </span>
        <span style={{ fontSize: 11, color: t.textMuted, fontWeight: 600 }}>/</span>
        {editing ? (
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
            <span style={{ fontSize: 11, color: t.textMuted }}>$</span>
            <input
              ref={inputRef}
              type="number"
              step="0.1"
              min="0"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={saveEdit}
              onKeyDown={handleKeyDown}
              style={{
                width: 48, fontSize: 12, fontWeight: 700, padding: '1px 4px',
                border: `1px solid ${P.indigo}`, borderRadius: 4,
                background: t.inputBg, color: t.inputColor, outline: 'none',
              }}
            />
            <span style={{ fontSize: 11, color: t.textMuted }}>M</span>
          </div>
        ) : (
          <span
            onClick={startEdit}
            style={{
              fontSize: 12, fontWeight: 700, cursor: 'pointer',
              color: hasCap ? t.textSecondary : P.indigo,
              borderBottom: `1px dashed ${hasCap ? t.textMuted : P.indigo}`,
              padding: '0 2px',
            }}
            title="Click to set capacity cap"
          >
            {hasCap ? $s(cap) : 'Set cap'}
          </span>
        )}
      </div>
      {/* Progress bar */}
      <div style={{
        width: '100%', height: 8, backgroundColor: t.dark ? '#2d3748' : '#e2e8f0',
        borderRadius: 4, overflow: 'hidden', position: 'relative',
      }}>
        {hasCap && (
          <div style={{
            width: `${Math.min(pct, 100)}%`, height: '100%',
            backgroundColor: barColor, borderRadius: 4,
            transition: 'width 0.4s ease, background-color 0.3s ease',
          }} />
        )}
        {/* Overage indicator */}
        {pct > 100 && (
          <div style={{
            position: 'absolute', right: 0, top: 0, bottom: 0,
            width: `${Math.min(pct - 100, 20)}%`,
            background: `repeating-linear-gradient(45deg, transparent, transparent 2px, ${P.rose}33 2px, ${P.rose}33 4px)`,
            borderRadius: '0 4px 4px 0',
          }} />
        )}
      </div>
      {/* Status label */}
      {hasCap && (
        <div style={{ fontSize: 10, fontWeight: 600, color: barColor, marginTop: 2 }}>
          {ratio >= 1 ? `Over by ${$s(current - cap)}` : `${Math.round(pct)}% utilized`}
        </div>
      )}
    </div>
  );
};

/* ═══ Progress Ring ═══ */
const MiniRing = ({ pct, size = 36, stroke = 4, color = P.indigo, t }) => {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const filled = Math.min(Math.max(pct || 0, 0), 100);
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={t.dark ? '#2d3748' : '#e2e8f0'} strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={circ - (filled / 100) * circ}
        strokeLinecap="round" style={{ transition: 'stroke-dashoffset .5s ease' }} />
    </svg>
  );
};

/* ═══ Drawdown Chart — dual axis bar+line chart ═══ */
const DrawdownChart = ({ drawdown, t }) => {
  if (!drawdown || drawdown.length === 0) return (
    <div style={{ padding: 20, textAlign: 'center', fontSize: 12, color: t.textMuted }}>No timeline data available</div>
  );

  const chartH = 140;
  const barW = Math.max(24, Math.min(48, 500 / drawdown.length));
  const chartW = Math.max(drawdown.length * (barW + 12) + 40, 300);
  const maxProj = Math.max(...drawdown.map(d => d.projects), 1);
  const maxVal = Math.max(...drawdown.map(d => d.value), 1);
  const padTop = 20, padBot = 30;
  const usable = chartH - padTop - padBot;

  // Build line path for value
  const points = drawdown.map((d, i) => {
    const x = i * (barW + 12) + barW / 2 + 20;
    const y = padTop + usable - (d.value / maxVal) * usable;
    return `${x},${y}`;
  });
  const linePath = points.length > 1 ? `M${points.join(' L')}` : '';

  return (
    <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
      <svg width={chartW} height={chartH} style={{ display: 'block' }}>
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => (
          <line key={i} x1={20} x2={chartW} y1={padTop + usable * (1 - pct)} y2={padTop + usable * (1 - pct)}
            stroke={t.dark ? '#2d3748' : '#f1f5f9'} strokeWidth={1} />
        ))}

        {/* Bars for project count */}
        {drawdown.map((d, i) => {
          const x = i * (barW + 12) + 20;
          const h = (d.projects / maxProj) * usable;
          return (
            <g key={i}>
              <rect x={x} y={padTop + usable - h} width={barW} height={h}
                rx={4} fill={P.indigo} opacity={0.2} />
              <rect x={x} y={padTop + usable - h} width={barW} height={Math.min(h, 4)}
                rx={2} fill={P.indigo} opacity={0.5} />
              {/* Project count label on bar */}
              <text x={x + barW / 2} y={padTop + usable - h - 4}
                textAnchor="middle" fontSize={10} fontWeight={700} fill={P.indigo}>
                {d.projects}
              </text>
              {/* Month label */}
              <text x={x + barW / 2} y={chartH - 4}
                textAnchor="middle" fontSize={9} fontWeight={600} fill={t.textMuted}>
                {fmtMonth(d.month)}
              </text>
            </g>
          );
        })}

        {/* Value line */}
        {linePath && (
          <path d={linePath} fill="none" stroke={P.emerald} strokeWidth={2.5}
            strokeLinecap="round" strokeLinejoin="round" />
        )}
        {/* Value dots */}
        {drawdown.map((d, i) => {
          const x = i * (barW + 12) + barW / 2 + 20;
          const y = padTop + usable - (d.value / maxVal) * usable;
          return <circle key={i} cx={x} cy={y} r={3.5} fill={P.emerald} stroke={t.bg} strokeWidth={2} />;
        })}
      </svg>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */
const WorkloadPage = ({ onOpenProject, currentUser }) => {
  const t = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedPM, setExpandedPM] = useState(null);
  const [sortBy, setSortBy] = useState('workload'); // workload | free | projects | name
  const [filterUrgency, setFilterUrgency] = useState('all'); // all | now | soon | later

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getWorkloadDashboard();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredPMs = useMemo(() => {
    if (!data) return [];
    let pms = [...data.pms];

    // Filter by urgency
    if (filterUrgency === 'now') pms = pms.filter(pm => pm.daysUntilFree <= 0);
    else if (filterUrgency === 'soon') pms = pms.filter(pm => pm.daysUntilFree > 0 && pm.daysUntilFree <= 14);
    else if (filterUrgency === 'later') pms = pms.filter(pm => pm.daysUntilFree > 14);

    // Sort
    if (sortBy === 'workload') pms.sort((a, b) => b.workloadScore - a.workloadScore);
    else if (sortBy === 'free') pms.sort((a, b) => a.daysUntilFree - b.daysUntilFree);
    else if (sortBy === 'projects') pms.sort((a, b) => b.activeProjects - a.activeProjects);
    else if (sortBy === 'name') pms.sort((a, b) => a.name.localeCompare(b.name));

    return pms;
  }, [data, sortBy, filterUrgency]);

  /* ═══ Styles ═══ */
  const card = {
    background: t.bg, borderRadius: 16,
    border: `1px solid ${t.cardBorder}`, padding: '24px 28px',
    marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,.03)',
  };
  const kpiCard = (color, bg) => ({
    ...card, padding: '20px 24px', marginBottom: 0, flex: '1 1 200px', minWidth: 180,
    borderLeft: `4px solid ${color}`, background: t.dark ? t.bg : bg,
  });
  const pillBtn = (active) => ({
    padding: '6px 16px', borderRadius: 20, border: 'none',
    background: active ? P.indigo : 'transparent',
    color: active ? '#fff' : t.textSecondary,
    fontWeight: 600, fontSize: 12, cursor: 'pointer', transition: 'all .15s',
  });

  if (loading) return (
    <div style={{ padding: '80px 20px', textAlign: 'center' }}>
      <div style={{
        width: 44, height: 44, border: `3px solid ${t.cardBorder}`,
        borderTopColor: P.indigo, borderRadius: '50%', margin: '0 auto 16px',
        animation: 'spin .7s linear infinite',
      }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ color: t.textSecondary, fontSize: 14, fontWeight: 500 }}>Loading workload data...</div>
    </div>
  );

  if (error) return (
    <div style={{ padding: '40px', textAlign: 'center' }}>
      <div style={{ color: P.rose, fontSize: 14 }}>{error}</div>
      <button onClick={loadData} style={{ marginTop: 12, padding: '8px 20px', borderRadius: 8, border: 'none', background: P.indigo, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Retry</button>
    </div>
  );

  if (!data) return null;
  const { summary } = data;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: t.heading, letterSpacing: '-.4px' }}>
            Team Workload
          </h2>
          <div style={{ fontSize: 12, color: t.textSecondary, marginTop: 6 }}>
            Capacity planning and workload distribution across project managers
          </div>
        </div>
        <button onClick={loadData} style={{
          padding: '8px 18px', borderRadius: 10, border: `1px solid ${t.cardBorder}`,
          background: t.bg, color: t.text, fontWeight: 600, fontSize: 13, cursor: 'pointer',
        }}>
          Refresh
        </button>
      </div>

      {/* KPI Summary Cards */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
        <div style={kpiCard(P.indigo, P.indigoBg)}>
          <div style={{ fontSize: 10, fontWeight: 700, color: t.textSecondary, textTransform: 'uppercase', letterSpacing: .8 }}>Active PMs</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: P.indigo, marginTop: 4 }}>{summary.totalActivePMs}</div>
        </div>
        <div style={kpiCard(P.emerald, P.emeraldBg)}>
          <div style={{ fontSize: 10, fontWeight: 700, color: t.textSecondary, textTransform: 'uppercase', letterSpacing: .8 }}>Active Projects</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: P.emerald, marginTop: 4 }}>{summary.totalActiveProjects}</div>
        </div>
        <div style={kpiCard(P.amber, P.amberBg)}>
          <div style={{ fontSize: 10, fontWeight: 700, color: t.textSecondary, textTransform: 'uppercase', letterSpacing: .8 }}>Open Tasks</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: P.amber, marginTop: 4 }}>{summary.totalActiveTasks}</div>
        </div>
        <div style={kpiCard(P.rose, P.roseBg)}>
          <div style={{ fontSize: 10, fontWeight: 700, color: t.textSecondary, textTransform: 'uppercase', letterSpacing: .8 }}>Need Work Soon</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: P.rose, marginTop: 4 }}>{summary.needWorkSoon}</div>
          <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>within 14 days</div>
        </div>
        <div style={kpiCard(P.sky, P.skyBg)}>
          <div style={{ fontSize: 10, fontWeight: 700, color: t.textSecondary, textTransform: 'uppercase', letterSpacing: .8 }}>Total Under Mgmt</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: P.sky, marginTop: 4 }}>{$s(summary.totalValueUnderMgmt)}</div>
          <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>contract value</div>
        </div>
      </div>

      {/* Filter & Sort Bar */}
      <div style={{ ...card, padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', gap: 4, background: t.bgAlt, borderRadius: 24, padding: 3 }}>
          {[
            { k: 'all', l: 'All' },
            { k: 'now', l: 'Needs Work Now' },
            { k: 'soon', l: 'Free Within 14d' },
            { k: 'later', l: 'Busy 14d+' },
          ].map(f => (
            <button key={f.k} style={pillBtn(filterUrgency === f.k)} onClick={() => setFilterUrgency(f.k)}>{f.l}</button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, textTransform: 'uppercase', letterSpacing: .5 }}>Sort by</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              padding: '6px 12px', borderRadius: 8, border: `1px solid ${t.cardBorder}`,
              background: t.inputBg, color: t.inputColor, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            <option value="workload">Workload Score</option>
            <option value="free">Soonest Available</option>
            <option value="projects">Most Projects</option>
            <option value="name">Name</option>
          </select>
        </div>
      </div>

      {/* PM Cards */}
      {filteredPMs.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', padding: 40, color: t.textMuted }}>
          No project managers match the current filter.
        </div>
      ) : (
        filteredPMs.map(pm => {
          const isExpanded = expandedPM === pm.name;
          const completionPct = pm.totalTasks > 0 ? Math.round((pm.completedTasks / pm.totalTasks) * 100) : 0;

          return (
            <div key={pm.name} style={{ ...card, padding: 0, overflow: 'hidden' }}>
              {/* PM Header Row */}
              <div
                onClick={() => setExpandedPM(isExpanded ? null : pm.name)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto auto auto auto auto auto',
                  gap: 20, padding: '20px 24px', alignItems: 'center', cursor: 'pointer',
                  transition: 'background .15s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = t.dark ? 'rgba(255,255,255,0.02)' : '#fafbfc'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                {/* Name + urgency badge */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{
                    width: 42, height: 42, borderRadius: 12,
                    background: `linear-gradient(135deg, ${P.indigo}, ${P.indigoL})`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontWeight: 800, fontSize: 16, flexShrink: 0,
                  }}>
                    {pm.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: t.heading }}>{pm.name}</div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 4, alignItems: 'center' }}>
                      <span style={{
                        padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                        color: urgencyColor(pm.daysUntilFree),
                        background: urgencyBg(pm.daysUntilFree),
                      }}>
                        {pm.daysUntilFree <= 0 ? 'Needs Work' : `Free in ${daysLabel(pm.daysUntilFree)}`}
                      </span>
                      {pm.overdueTasks > 0 && (
                        <span style={{
                          padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                          color: P.rose, background: P.roseBg,
                        }}>
                          {pm.overdueTasks} overdue
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Projects count */}
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: t.heading }}>{pm.activeProjects}</div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: t.textMuted, textTransform: 'uppercase' }}>Projects</div>
                </div>

                {/* Capacity cap + under management */}
                <CapacityBar
                  current={pm.totalUnderMgmt}
                  cap={pm.capacityCap || 0}
                  onCapChange={async (newCap) => {
                    try {
                      await updatePMCapacity(pm.name, newCap);
                      // Update local state
                      setData(prev => ({
                        ...prev,
                        pms: prev.pms.map(p => p.name === pm.name ? { ...p, capacityCap: newCap } : p),
                      }));
                    } catch (err) {
                      console.error('Failed to update PM cap:', err);
                    }
                  }}
                  t={t}
                />

                {/* Task progress ring */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <MiniRing pct={completionPct} color={P.emerald} t={t} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: t.heading }}>{pm.completedTasks}/{pm.totalTasks}</div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: t.textMuted, textTransform: 'uppercase' }}>Tasks Done</div>
                  </div>
                </div>

                {/* In progress */}
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: P.sky }}>{pm.inProgressTasks}</div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: t.textMuted, textTransform: 'uppercase' }}>Active</div>
                </div>

                {/* Workload bar */}
                <div>
                  <WorkloadBar score={pm.workloadScore} t={t} />
                  <div style={{ fontSize: 10, fontWeight: 600, color: t.textMuted, textTransform: 'uppercase', marginTop: 2 }}>Workload</div>
                </div>

                {/* Expand chevron */}
                <div style={{ fontSize: 14, color: t.textMuted, transition: 'transform .2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)' }}>
                  ▾
                </div>
              </div>

              {/* Expanded project breakdown */}
              {isExpanded && (
                <div style={{
                  borderTop: `1px solid ${t.cardBorder}`,
                  background: t.dark ? 'rgba(255,255,255,0.01)' : '#fafbfc',
                  padding: '16px 24px',
                }}>
                  {/* Timeline summary */}
                  <div style={{ display: 'flex', gap: 24, marginBottom: 16, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: t.textMuted }}>Latest finish:</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: t.heading }}>{fmtDate(pm.latestFinishDate)}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: t.textMuted }}>Next task starts:</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: t.heading }}>{fmtDate(pm.nextTaskStart)}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: t.textMuted }}>Upcoming (14d):</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: P.amber }}>{pm.upcomingTasks}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: t.textMuted }}>Not started:</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: t.heading }}>{pm.notStartedTasks}</span>
                    </div>
                  </div>

                  {/* Drawdown Chart */}
                  {pm.drawdown && pm.drawdown.length > 0 && (
                    <div style={{
                      marginBottom: 20, padding: '16px 20px',
                      background: t.bg, borderRadius: 12, border: `1px solid ${t.cardBorder}`,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: t.heading }}>Project & Value Drawdown</h4>
                        <div style={{ display: 'flex', gap: 16, fontSize: 11, fontWeight: 600 }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <span style={{ width: 10, height: 10, borderRadius: 3, background: P.indigo, opacity: 0.3, display: 'inline-block' }} />
                            <span style={{ color: t.textMuted }}>Projects</span>
                          </span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <span style={{ width: 14, height: 3, borderRadius: 2, background: P.emerald, display: 'inline-block' }} />
                            <span style={{ color: t.textMuted }}>Under Mgmt Value</span>
                          </span>
                        </div>
                      </div>
                      <DrawdownChart drawdown={pm.drawdown} t={t} />
                    </div>
                  )}

                  {/* Project rows */}
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1fr', gap: 0, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: t.textMuted, padding: '8px 12px', borderBottom: `1px solid ${t.cardBorder}` }}>
                    <div>Project</div>
                    <div style={{ textAlign: 'right' }}>Contract Value</div>
                    <div style={{ textAlign: 'center' }}>Tasks</div>
                    <div style={{ textAlign: 'center' }}>Active</div>
                    <div style={{ textAlign: 'center' }}>Overdue</div>
                    <div style={{ textAlign: 'center' }}>Complete</div>
                    <div style={{ textAlign: 'center' }}>Latest Finish</div>
                  </div>
                  {pm.projectBreakdown.map(proj => (
                    <div
                      key={proj.id}
                      onClick={() => onOpenProject && onOpenProject(proj.id)}
                      style={{
                        display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr 1fr',
                        gap: 0, padding: '12px', alignItems: 'center', fontSize: 13,
                        borderBottom: `1px solid ${t.cardBorder}`,
                        cursor: 'pointer', transition: 'background .15s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = t.dark ? 'rgba(255,255,255,0.03)' : '#f1f5f9'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {proj.logo_url ? (
                          <img src={proj.logo_url} alt="" style={{ width: 28, height: 28, borderRadius: 6, objectFit: 'cover', border: `1px solid ${t.cardBorder}` }} />
                        ) : (
                          <div style={{
                            width: 28, height: 28, borderRadius: 6, background: t.bgAlt,
                            border: `1px solid ${t.cardBorder}`, display: 'flex', alignItems: 'center',
                            justifyContent: 'center', fontSize: 12, fontWeight: 700, color: t.textMuted,
                          }}>
                            {(proj.project_name || '?')[0].toUpperCase()}
                          </div>
                        )}
                        <div>
                          <div style={{ fontWeight: 600, color: t.heading }}>{proj.project_name}</div>
                          {proj.project_number && <div style={{ fontSize: 11, color: t.textMuted }}>#{proj.project_number}</div>}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', fontWeight: 700, color: t.heading }}>{$f(proj.contract_value)}</div>
                      <div style={{ textAlign: 'center', fontWeight: 600, color: t.text }}>{proj.totalTasks}</div>
                      <div style={{ textAlign: 'center', fontWeight: 700, color: P.sky }}>{proj.activeTasks}</div>
                      <div style={{ textAlign: 'center', fontWeight: 700, color: proj.overdueTasks > 0 ? P.rose : t.textMuted }}>
                        {proj.overdueTasks}
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <span style={{ fontWeight: 600, color: t.text }}>{proj.percentComplete}%</span>
                      </div>
                      <div style={{ textAlign: 'center', fontSize: 12, color: t.textSecondary }}>
                        {fmtDate(proj.latestFinishDate)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default WorkloadPage;
